import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Chip,
  Skeleton
} from '@mui/material';
import TaskIcon from '@mui/icons-material/Task';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import IntelligentEmailActivity from './IntelligentEmailActivity';

interface Task {
  id: string;
  description: string;
  is_completed: boolean;
  due_date: string | null;
  created_at: string;
  deal_id: string;
  deal_name?: string;
}

interface Activity {
  id: string;
  activity_type: string;
  subject?: string;
  content?: string;
  created_at: string;
  contact_id: string;
  sender?: string;
  recipients?: string;
  summary?: string;
  sentiment?: 'Positive' | 'Neutral' | 'Negative';
  detected_action_items?: string[];
  type?: 'DEAL_CREATED' | 'DEAL_UPDATED' | 'CONTACT_ADDED' | 'TASK_COMPLETED' | 'EMAIL';
  entity_id?: string;
  entity_name?: string;
  description?: string;
}

interface CentralFeedProps {
  tasks: Task[];
  activities: Activity[];
  isLoading?: boolean;
}

type FeedItem = {
  id: string;
  type: 'task' | 'activity';
  date: Date;
  original: Task | Activity;
};

export default function CentralFeed({ tasks, activities, isLoading = false }: CentralFeedProps) {
  const [filter, setFilter] = useState<'all' | 'tasks' | 'activities'>('all');
  
  const feedItems = useMemo(() => {
    // Combine tasks and activities
    const items: FeedItem[] = [
      ...tasks.map(task => ({
        id: `task-${task.id}`,
        type: 'task' as const,
        date: new Date(task.created_at),
        original: task
      })),
      ...activities.map(activity => ({
        id: `activity-${activity.id}`,
        type: 'activity' as const,
        date: new Date(activity.created_at),
        original: activity
      }))
    ];
    
    // Sort by date (newest first)
    return items.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [tasks, activities]);
  
  const filteredItems = useMemo(() => {
    if (filter === 'all') return feedItems;
    return feedItems.filter(item => item.type === filter);
  }, [feedItems, filter]);
  
  const getActivityIcon = (activity: Activity) => {
    if (activity.activity_type === 'Email') {
      return <EmailIcon color="primary" />;
    }
    
    switch (activity.type) {
      case 'DEAL_CREATED':
        return <TrendingUpIcon color="success" />;
      case 'DEAL_UPDATED':
        return <TrendingUpIcon color="primary" />;
      case 'CONTACT_ADDED':
        return <PersonAddIcon color="info" />;
      case 'TASK_COMPLETED':
        return <AssignmentTurnedInIcon color="success" />;
      default:
        return <TrendingUpIcon />;
    }
  };
  
  // Check if an activity is an AI-enhanced email
  const isIntelligentEmail = (activity: Activity) => {
    return activity.activity_type === 'Email' && 
           (activity.summary || activity.sentiment || 
           (activity.detected_action_items && activity.detected_action_items.length > 0));
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Recent Activity</Typography>
          <Skeleton animation="wave" height={40} />
          <Skeleton animation="wave" height={40} />
          <Skeleton animation="wave" height={40} />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Recent Activity</Typography>
          <Box>
            <Chip 
              label="All" 
              color={filter === 'all' ? 'primary' : 'default'} 
              onClick={() => setFilter('all')}
              sx={{ mr: 1 }}
            />
            <Chip 
              label="Tasks" 
              color={filter === 'tasks' ? 'primary' : 'default'} 
              onClick={() => setFilter('tasks')}
              sx={{ mr: 1 }}
            />
            <Chip 
              label="Activities" 
              color={filter === 'activities' ? 'primary' : 'default'} 
              onClick={() => setFilter('activities')}
            />
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {filteredItems.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No recent activity to display
          </Typography>
        ) : (
          <List>
            {filteredItems.map((item) => {
              // For intelligent email activities, use the IntelligentEmailActivity component
              if (item.type === 'activity' && isIntelligentEmail(item.original as Activity)) {
                return (
                  <Box key={item.id} sx={{ mb: 2 }}>
                    <IntelligentEmailActivity activity={item.original as Activity} />
                  </Box>
                );
              }
              
              // For other items, use the regular list item
              return (
                <ListItem key={item.id} divider>
                  <ListItemIcon>
                    {item.type === 'task' 
                      ? <TaskIcon color={(item.original as Task).is_completed ? 'disabled' : 'warning'} /> 
                      : getActivityIcon(item.original as Activity)
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      item.type === 'task' 
                        ? (
                          <Box component={Link} to={`/deals/${(item.original as Task).deal_id}`} sx={{ textDecoration: 'none', color: 'inherit' }}>
                            {(item.original as Task).description}
                          </Box>
                        )
                        : (item.original as Activity).description || (item.original as Activity).subject
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {item.date.toLocaleString()}
                        {item.type === 'task' && (item.original as Task).deal_name && (
                          <> | Deal: {(item.original as Task).deal_name}</>
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
} 